import * as z from 'zod';

import { ContentPermissionLevelOrNoneSchema } from '../content';
import { Timestamp } from '../db/firestore';
import type { OrganizationMemberRoleOrGuest } from './roles';

/**
 * Maximum number of emails allowed in a single request to the invite API endpoint.
 */
export const MAX_INVITE_EMAILS = 100;

/**
 * Membership of an user in an organization.
 */
export const OrganizationMemberSchema = z.object({
    organization: z.string(),
    user: z.string(),
    role: ContentPermissionLevelOrNoneSchema,
    joinedAt: z.instanceof(Timestamp),
    lastSeenAt: z.instanceof(Timestamp).optional(),
    /**
     * Reference to the link-invite used during signup
     */
    invite: z.string().optional(),
    /**
     * If this membership was created because of an organization
     * transfer, this is the key of the source org.
     */
    transferredFrom: z.string().optional(),
    /**
     * Set the disabled state of a member if needed. A user is defined as enabled by default and will only be frozen
     * if this property is true.
     */
    disabled: z.boolean().optional(),
});

export type OrganizationMember = z.infer<typeof OrganizationMemberSchema>;

/**
 * Create a new membership for an user.
 */
export function createOrgMembership(args: {
    org: string;
    user: string;
    role?: OrganizationMemberRoleOrGuest;
    invite?: string;
    transferredFrom?: string;
    disabled?: boolean;
}): OrganizationMember {
    const membership: OrganizationMember = {
        organization: args.org,
        user: args.user,
        role: args.role || null,
        joinedAt: Timestamp.now(),
        disabled: args.disabled,
    };

    if (args.invite) {
        membership.invite = args.invite;
    }

    if (args.transferredFrom) {
        membership.transferredFrom = args.transferredFrom;
    }

    return membership;
}

/**
 * Check if a membership has changed in a way that requires recomputing permissions.
 */
export function hasMembershipPermissionsChanges(
    before: OrganizationMember,
    after: OrganizationMember
): boolean {
    return (
        before.role !== after.role ||
        before.disabled !== after.disabled ||
        before.invite !== after.invite
    );
}
